import React, { useEffect, useState } from 'react'
import { Alert, Button, Snackbar } from '@mui/material'
import * as Sentry from '@sentry/react'
import { RELEASE } from '../bootstrapping/sentry'
import { CHECK_INTERVAL_MS, fetchLiveRelease, isNewBuildLive } from '../utils/newVersion'

/**
 * DBS-350: when a newer build is live, offers a reload (never forces one: a form may be half filled in) and tags
 * further Sentry events build_outdated=yes, so errors from tabs left open across a deploy are easy to tell apart.
 * Checks when the tab comes back into view and every 10 minutes.
 */
const NewVersionNotice: React.FC = () => {
  const [liveRelease, setLiveRelease] = useState<string>()
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (RELEASE === 'development') return
    let stopped = false
    const check = async () => {
      if (document.visibilityState !== 'visible') return
      const live = await fetchLiveRelease()
      if (stopped || !isNewBuildLive(RELEASE, live)) return
      Sentry.setTag('build_outdated', 'yes')
      Sentry.setTag('build_live', live!)
      setLiveRelease(live)
    }
    const timer = window.setInterval(check, CHECK_INTERVAL_MS)
    document.addEventListener('visibilitychange', check)
    return () => {
      stopped = true
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', check)
    }
  }, [])

  return (
    <Snackbar open={liveRelease !== undefined && !dismissed} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
      <Alert
        severity="info"
        variant="filled"
        data-testid="new-version-notice"
        onClose={() => setDismissed(true)}
        action={<Button color="inherit" size="small" onClick={() => window.location.reload()}>Reload</Button>}
      >
        A new version of this portal is available. Save any changes, then reload.
      </Alert>
    </Snackbar>
  )
}

export default NewVersionNotice
